import { useEffect, useState } from "react";
import { RefreshCw, CloudOff } from "lucide-react";
import { registerServiceWorker, applyUpdate } from "@/lib/swUpdate";

// Two small, related status surfaces:
//   • "Update ready" — a new version has installed and is waiting. The swap
//     happens only when the user taps, so a half-written dose log is never
//     thrown away by a surprise reload.
//   • "Offline" — the app keeps working from its cache, but the assistant and
//     anything else networked won't, so say so plainly instead of letting
//     features look broken.
export default function UpdatePrompt() {
  const [registration, setRegistration] = useState(null);
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  const [applying, setApplying] = useState(false);

  useEffect(() => registerServiceWorker(setRegistration), []);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  if (registration) {
    return (
      <div className="fixed left-3 right-3 z-50" style={{ bottom: "calc(var(--tabbar-h) + var(--sab) + 14px)" }} data-testid="update-prompt">
        <div className="mx-auto max-w-md card-soft p-3.5 animate-rise flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/12 text-primary flex items-center justify-center shrink-0">
            <RefreshCw className={applying ? "h-5 w-5 animate-spin" : "h-5 w-5"} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Update ready</p>
            <p className="text-xs text-muted-foreground">A newer version of Meditrax is installed.</p>
          </div>
          <button
            onClick={() => { setApplying(true); applyUpdate(registration); }}
            disabled={applying}
            data-testid="update-reload"
            className="pressable shrink-0 h-9 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
          >
            {applying ? "Updating…" : "Reload"}
          </button>
        </div>
      </div>
    );
  }

  if (offline) {
    return (
      <div className="fixed left-3 right-3 z-40 pointer-events-none" style={{ bottom: "calc(var(--tabbar-h) + var(--sab) + 14px)" }} data-testid="offline-indicator">
        <div className="mx-auto w-fit rounded-full bg-muted text-muted-foreground px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 shadow-sm">
          <CloudOff className="h-3.5 w-3.5" />
          Offline — your data is all here; the assistant needs a connection
        </div>
      </div>
    );
  }

  return null;
}
